import React, {
  useCallback,
  useImperativeHandle,
  useRef,
  useState
} from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import { SubmitHandler, useForm } from 'react-hook-form';

import { AbsenceService } from 'components/services/AbsenceService';
import { AbsenceTemplate } from 'constants/AbsenceTemplate';

import { AbsenceSchema } from '../constants/schemas';
import { handleApiErrors } from '../helpers';
import { useApiCall } from '../hooks/useApiCall';
import { Absence } from '../types/interfaces';
import { AbsenceProps } from '../types/props';

import ConfirmationDialog from './shared/ConfirmationDialog';
import DeleteConfirmationDialog from './shared/DeleteConfirmationDialog';

import '../ui/css/AbsenceCard.css';

interface RefInterface {
  handleCancel: () => void;
}

const AbsenceCard = React.forwardRef<RefInterface, AbsenceProps>(
  (
    { scheduleProps, onAdd, onUpdate, onDelete, closeDialog }: AbsenceProps,
    ref: React.Ref<RefInterface>
  ) => {
    const {
      register,
      handleSubmit,
      watch,
      reset,
      setError,
      formState: { isDirty, errors }
    } = useForm({
      resolver: zodResolver(AbsenceSchema),
      defaultValues: {
        ...scheduleProps,
        shouldRemoveShift: !scheduleProps.shouldRemoveShift
      }
    });

    const [showDialog, setShowDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const toast = useRef<Toast>(null);
    const absenceName = watch('name');
    const scheduleId = watch('id');
    const shouldRemoveShift = watch('shouldRemoveShift');

    const loadScheduleData = useCallback(async () => {
      const mergeWithDefaults = (absence: Absence): Absence => ({
        ...AbsenceTemplate,
        ...absence
      });

      if (scheduleProps.id && scheduleProps.id !== 0) {
        try {
          const response = (
            await AbsenceService.getAbsenceById(Number(scheduleProps.id))
          ).data;
          if (response && response.value) {
            response.value.shouldRemoveShift =
              !response.value.shouldRemoveShift;
            reset(mergeWithDefaults(response.value));
          } else {
            reset(AbsenceTemplate);
            console.error('Error loading absence:', response);
          }
        } catch (error) {
          console.error('Failed to load absence:', error);
          reset(AbsenceTemplate);
        }
      } else {
        reset(AbsenceTemplate);
      }
    }, [scheduleProps.id, reset]);

    useApiCall(loadScheduleData);

    const onSubmit: SubmitHandler<Absence> = useCallback(
      async (data) => {
        const newData = { ...data };
        newData.shouldRemoveShift = !newData.shouldRemoveShift;
        const dataId = data.id;
        let result;

        if (onUpdate && scheduleId) {
          result = await onUpdate(Number(dataId), newData);
        }
        if (onAdd && !scheduleId) {
          newData.id = 0;
          result = await onAdd(newData);
        }
        if (result?.data.isSuccess) {
          closeDialog?.();
        } else {
          handleApiErrors({
            error: result?.data.error,
            setError,
            fieldMapping: {
              SameName: {
                field: 'name',
                message:
                  'Запись с таким названием уже есть в справочнике. Пожалуйста, укажите уникальное название вида отсутствия'
              },
              SameShortName: {
                field: 'shortName',
                message:
                  'Запись с таким коротким названием уже есть в справочнике. Пожалуйста, укажите уникальное короткое название вида отсутствия'
              }
            }
          });

          if (!result?.data.error) {
            toast.current?.show({
              severity: 'error',
              summary: 'Ошибка сохранения',
              detail: 'Не удалось сохранить вид отсутствия'
            });
          }
        }
      },
      [closeDialog, onAdd, onUpdate, setError]
    );

    const handleCancel = () => {
      if (isDirty) {
        setShowDialog(true);
      }
      if (closeDialog && !isDirty) {
        closeDialog();
      }
    };

    useImperativeHandle(ref, () => {
      return {
        handleCancel
      };
    });

    const handleDelete = () => {
      setShowDeleteDialog(true);
    };

    const handleDeleteCancellation = () => {
      setShowDeleteDialog(false);
    };

    const handleDeleteConfirmation = async () => {
      if (onDelete) {
        await onDelete(scheduleId);
        closeDialog && closeDialog();
      }
      setShowDeleteDialog(false);
    };

    return (
      <div className="dialog-tabless-card absence-card absence-card__roles">
        <Toast ref={toast} />
        <div className=" gap-3 pb-0">
          <Divider />
          <div className="absence-card__column">
            <div className="absence-card">
              <div className="flex flex-row gap-5">
                <div className="absence-card__row w-full">
                  <div className=" flex flex-row align-items-center gap-3 w-full">
                    <InputSwitch
                      checked={shouldRemoveShift}
                      {...register('shouldRemoveShift')}
                    />
                    <label
                      className="col-fixed fixed-labels"
                      htmlFor="shouldRemoveShift"
                    >
                      Не удаляет смены
                    </label>
                  </div>
                  <p
                    className={`card-errors ${
                      errors.name ? 'text-red-500' : 'text-transparent'
                    } `}
                  >
                    {errors.name ? errors.name.message : ' '}
                  </p>
                </div>
              </div>

              <div className="flex flex-row gap-5">
                <div className="absence-card__row w-full">
                  <div className=" flex flex-row justify-content-between align-items-center gap-3 w-full">
                    <label
                      style={{ width: '140px', padding: '0' }}
                      className="col-fixed fixed-labels"
                      htmlFor="name"
                    >
                      Название<span className="required">*</span>:
                    </label>
                    <InputText
                      className="oneline-input--grid user-card__input col"
                      {...register('name')}
                    />
                  </div>
                  <p
                    className={`card-errors ${
                      errors.name ? 'text-red-500' : 'text-transparent'
                    } `}
                  >
                    {errors.name ? errors.name.message : ' '}
                  </p>
                </div>
              </div>

              <div className="flex flex-row gap-5">
                <div className=" w-full">
                  <div className="flex flex-row justify-content-between align-items-center gap-3 w-full">
                    <label
                      style={{ width: '140px', padding: '0' }}
                      className="col-fixed fixed-labels"
                      htmlFor="shortName"
                    >
                      Короткое название<span className="required">*</span>:
                    </label>
                    <InputText
                      className="oneline-input--grid user-card__input col"
                      {...register('shortName')}
                    />
                  </div>
                  <p
                    className={`card-errors ${
                      errors.shortName ? 'text-red-500' : 'text-transparent'
                    } `}
                  >
                    {errors.shortName ? errors.shortName.message : ' '}
                  </p>
                </div>
              </div>
            </div>

            <Tooltip
              target=".p-inputgroup-addon"
              mouseTrack
              mouseTrackLeft={10}
            />
          </div>
        </div>

        <div className="absence-card__buttons default-card__buttons">
          <div className="flex gap-3">
            <Button
              label="Сохранить"
              icon="pi pi-check"
              onClick={handleSubmit(onSubmit)}
            />
            <Button
              label="Удалить"
              icon="pi pi-trash"
              onClick={handleDelete}
              className="p-button-danger"
            />
            <Button
              label="Отмена"
              icon="pi pi-times"
              onClick={handleCancel}
              className="p-button-secondary"
            />
          </div>
        </div>

        <ConfirmationDialog
          visible={showDialog}
          onCancel={() => setShowDialog(false)}
          onContinue={() => {
            setShowDialog(false);
            closeDialog?.();
          }}
        />

        <DeleteConfirmationDialog
          visible={showDeleteDialog}
          onHide={handleDeleteCancellation}
          onConfirm={handleDeleteConfirmation}
          entityType="вид отсутсвия"
          entityName={absenceName}
        />
      </div>
    );
  }
);

AbsenceCard.displayName = 'AbsenceCard';

export default AbsenceCard;
